import dashboardService from 'ApiService/Admin/dashboardService';
import BarChart from 'components/charts/BarChart';
import Card from 'components/card';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import InputField from 'components/fields/InputField';

const BatchPlacementChart = ({ selectedCourse, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [batchData, setBatchData] = useState([]);
  const [barChartData, setBarChartData] = useState([
    { name: 'Total Students', data: [] },
    { name: 'Placed Students', data: [] },
  ]);
  const [xAxisCategories, setXAxisCategories] = useState([]);

  const [startDate, setStartDate] = useState(selectedCourse.startDate || '');
  const [endDate, setEndDate] = useState(selectedCourse.endDate || '');

  const fetchBatchData = async (start = startDate, end = endDate) => {
    setLoading(true);
    try {
      const response = await dashboardService.getBatchPlacementData(
        selectedCourse.courseId,
        start,
        end
      );

      if (response.data.success) {
        const { placementChartData } = response.data;

        setBatchData(placementChartData);

        const totalStudentsData = [];
        const placedStudentsData = [];
        const batchNames = [];

        placementChartData.forEach((batch) => {
          batchNames.push(`Batch ${batch.batchName}`);
          totalStudentsData.push(batch.totalStudents);
          placedStudentsData.push(batch.placedStudents);
        });

        setBarChartData([
          { name: 'Total Students', data: totalStudentsData },
          { name: 'Placed Students', data: placedStudentsData },
        ]);
        setXAxisCategories(batchNames);
      } else {
        toast.error('Failed to fetch batch placement data');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatchData();
  }, [selectedCourse]);

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    fetchBatchData('', '');
  };

  const handleApply = () => {
    fetchBatchData(startDate, endDate);
  };

  return (
    <Card extra="pb-7 p-4" style={{ border: '3px solid #36A2EB' }}>
      <div className="mb-4">
        <button
          onClick={onClose}
          className="text-sm font-medium text-blue-500 hover:underline"
        >
          &larr; Back to Placement Chart
        </button>
        <h2 className="mt-2 text-lg font-bold text-navy-700 dark:text-white">
          Placement Details for {selectedCourse.courseName}
        </h2>
      </div>

      {/* Date Filters */}
      <div className="mb-4 flex flex-col gap-4 md:flex-row">
        <div>
          <InputField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
          />
        </div>
        <div>
          <InputField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
          />
        </div>
        <div className="flex items-end gap-2">
          <button
            onClick={handleApply}
            className="rounded bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700"
          >
            Apply
          </button>
          <button
            onClick={handleReset}
            className="text-black rounded-md bg-gray-600 px-4 py-2 transition hover:bg-gray-800"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="h-[300px] w-full">
        {loading ? (
          <div className="flex h-full items-center justify-center bg-white bg-opacity-70 dark:bg-navy-800 dark:bg-opacity-70">
            <div className="border-primary border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-solid"></div>
          </div>
        ) : batchData.length > 0 ? (
          <BarChart
            chartData={barChartData}
            chartOptions={{
              chart: { toolbar: { show: false } },
              tooltip: { theme: 'dark' },
              xaxis: {
                categories: xAxisCategories,
                labels: {
                  show: true,
                  style: {
                    colors: '#A3AED0',
                    fontSize: '12px',
                    fontWeight: '500',
                  },
                },
                axisBorder: { show: false },
                axisTicks: { show: false },
              },
              yaxis: { show: false },
              grid: {
                show: false,
                strokeDashArray: 5,
                yaxis: { lines: { show: true } },
                xaxis: { lines: { show: false } },
              },
              fill: {
                type: 'gradient',
                gradient: {
                  type: 'vertical',
                  shadeIntensity: 1,
                  opacityFrom: 0.7,
                  opacityTo: 0.9,
                  colorStops: [
                    [
                      { offset: 0, color: 'var(--color-500)', opacity: 1 },
                      { offset: 100, color: '#6F32E0', opacity: 0.0 },
                    ],
                    [
                      { offset: 0, color: '#FF6384', opacity: 1 },
                      {
                        offset: 100,
                        color: 'rgba(255, 255, 255, 1)',
                        opacity: 0.0,
                      },
                    ],
                  ],
                },
              },
              dataLabels: { enabled: false },
              plotOptions: {
                bar: {
                  borderRadius: 5,
                  columnWidth: '20px',
                },
              },
              legend: {
                markers: {
                  fillColors: ['var(--color-500)', '#FF6384'],
                },
                labels: {
                  colors: 'dark:#CBD5E0',
                },
              },
            }}
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <p>No batch placement data found</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default BatchPlacementChart;
